import { Box, createStyles, Group, Modal } from '@mantine/core';
import { useDebouncedValue, useDisclosure } from '@mantine/hooks';
import { Button, IconButton, Text } from '@repo/foundations';
import type { EdgeProps } from '@xyflow/react';
import { BaseEdge, EdgeLabelRenderer } from '@xyflow/react';
import { memo, useCallback, useMemo } from 'react';
import { useLineageFlowContext } from '../../context/LineageFlowProvider/LineageFlowContext';
import { getLineageFlowBezierPath } from '../../utils/getLineageFlowBezierPath';
import type { LineageFlowEdge } from '../EntityEdge/EntityEdge.types';

const useStyles = createStyles(
	(theme, { labelX, labelY }: { labelX: number; labelY: number }) => ({
		container: {
			position: 'absolute',
			transform: `translate(-50%, -50%) translate(${labelX}px,${labelY}px)`,
			pointerEvents: 'all',
		},
		manualIndicator: {
			width: theme.other.space[1.5],
			height: theme.other.space[1.5],
			backgroundColor: theme.other.getColor('border/tertiary/default'),
			position: 'relative',
			transform: 'rotate(45deg)',
		},
		footer: {
			flexDirection: 'row-reverse',
			borderTop: `1px solid ${theme.other.getColor('border/secondary/default')}`,
			marginLeft: `-${theme.spacing.lg}`,
			marginRight: `-${theme.spacing.lg}`,
			paddingLeft: theme.spacing.lg,
			paddingRight: theme.spacing.lg,
			paddingTop: theme.spacing.md,
		},
	})
);

export const ManualEdge = memo(
	({
		id,
		sourceX,
		sourceY,
		targetX,
		targetY,
		sourcePosition,
		targetPosition,
		markerEnd,
		data,
	}: EdgeProps<LineageFlowEdge>) => {
		const [opened, { open, close }] = useDisclosure(false);
		const [debouncedIsHovered] = useDebouncedValue(data?.hovered, 100);
		const [edgePath, labelX, labelY] = getLineageFlowBezierPath({
			sourceX,
			sourceY,
			sourcePosition,
			targetX,
			targetY,
			targetPosition,
		});

		const { createAccess, onDeleteManualLineage } = useLineageFlowContext();

		const { classes, cx, theme } = useStyles({
			labelX,
			labelY,
		});

		const style = useMemo(
			() => ({
				stroke: theme.other.getColor('border/primary/default'),
				strokeWidth: 1,
			}),
			[theme]
		);

		const handleDelete = useCallback(() => {
			onDeleteManualLineage({ edgeId: id });
			close();
		}, [onDeleteManualLineage, id, close]);

		return (
			<>
				<BaseEdge id={id} path={edgePath} markerEnd={markerEnd} style={style} />
				<EdgeLabelRenderer>
					<Box className={cx(classes.container, 'nodrag', 'nopan')}>
						{createAccess && debouncedIsHovered ? (
							<IconButton
								variant="tertiary"
								iconName="trash"
								size="sm"
								onClick={open}
							/>
						) : (
							<Box className={classes.manualIndicator} />
						)}
					</Box>
				</EdgeLabelRenderer>
				<Modal opened={opened} onClose={close} title="Delete Lineage?">
					<Text mb="md">You can't undo this action.</Text>
					<Group className={classes.footer}>
						<Button variant="primary" tone="critical" onClick={handleDelete}>
							Delete
						</Button>
						<Button variant="default" onClick={close}>
							Cancel
						</Button>
					</Group>
				</Modal>
			</>
		);
	}
);
